import React, { ReactNode, useEffect, useState } from "react";
import {
  PlusOutlined,
  EditOutlined,
  MoreOutlined,
  AppstoreOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { Modal, Tabs, Form, Input, Spin, Button, Space, Radio, Dropdown, notification } from 'antd';
import styled from "styled-components";

import { ancanaApi, ancanaApiHeaders } from "../api/ancanaApi";
import DraggableTabs from "./DraggableTabs";
import DraggableUpload from "./DraggableUpload";
import axios from "axios";
import useContentType from "../hooks/useContentType";


interface RecordGalleriesModalProps {
  recordPath: string;
  recordId: string | number;
  recordName: string;
  children?: ReactNode;
}

function compareNumbers(a: any, b: any) {
  return a.sort_index - b.sort_index;
}

const Container = styled.div`
  overflow: visible;

  .ant-tabs {
    // overflow: scroll !important;
  }

  .ant-radio-button-wrapper {
    margin-bottom: 0;
  }

  .ant-tabs-nav {
    margin-bottom: 12px;
  }
`;

function RecordGalleries({ recordPath, recordId, recordName, children }: RecordGalleriesModalProps) {
  const [activeKey, setActiveKey] = useState<string | undefined>();
  const [loadingGalleries, setLoadingGalleries] = useState(false);
  const [loadingImages, setLoadingImages] = useState(false);
  const [galleries, setGalleries] = useState<any>([]);
  const [allImages, setAllImages] = useState<any[]>([]);
  const [currentFiles, setCurrentFiles] = useState<any>([]);
  const [previewImage, setPreviewImage] = useState();
  const [previewImageFile, setPreviewImageFile] = useState<any>();
  const [previewOpen, setPreviewOpen] = useState(false);
  const [form] = Form.useForm();
  const basePath = `${recordPath}/${recordId}/galleries`;
  const baseImgPath = `${recordPath}/${recordId}/gallery-images`;
  const galleryContentType = useContentType(basePath);
  const galleryImgContentType = useContentType(baseImgPath);
  const [galleryEditForm] = Form.useForm();
  const [imageEditForm] = Form.useForm();

  const fetchGalleries = () => {
    setLoadingGalleries(true);
    ancanaApi.get(basePath+'/').then(res => {
      setGalleries(res.data);
      if (res.data.length > 0) setActiveKey(res.data[0].id);
      setLoadingGalleries(false);
    })
    setLoadingImages(true);
    ancanaApi.get(baseImgPath+'/').then(res => {
      setAllImages(res.data);
      setLoadingImages(false);
    });
  }

  const handleTabEdit = (key: any, action: string) => {
    switch (action) {
      case "remove":
        const galToDelete = galleries.find((gal: any) => gal.id == key);
        Modal.confirm({
          title: `Confirm delete of "${galToDelete?.name}" gallery`,
          content: 'This will delete all the pictures inside',
          okText: 'Delete',
          onOk: () => {
            if (galToDelete) {
              ancanaApi.delete(`${basePath}/${galToDelete?.id}`).then(res => {
                setGalleries(galleries.filter((gal: any) => gal.id != key));
                if (galleries.length > 0) {
                  setActiveKey(galleries[0].id);
                }
              });
            }
          }
        });
        break;
      case "add":
        const addModal = Modal.confirm({
          title: `Create a new gallery`,
          content: (
            <Form
              layout="vertical"
              form={form}
              onFinish={(data: any) => {
                ancanaApi.post(`${basePath}/`, data).then(res => {
                  setGalleries([...galleries, res.data]);
                  addModal.destroy();
                });
              }}
            >
              <Form.Item label="name" name="name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
              <Form.Item name="description__en" label="description english">
                <Input.TextArea />
              </Form.Item>
              <Form.Item name="description__es" label="description spanish">
                <Input.TextArea />
              </Form.Item>
            </Form>
          ),
          okText: "Create",
          icon: null,
          onOk: (close: any) => form.submit()
        });
        break;
    }
  }

  const handleTabClick = async (key: string) => {
    galleryEditForm.resetFields();
    const selectedGal = galleries.find((gal: any) => gal.id == key);
    const localizedTexts = (await ancanaApi.get(`localized-texts/?content_type=${galleryContentType?.id}&object_id=${selectedGal.id}&page_size=50`) as any).data.results;
    const editModal = Modal.confirm({
      title: `Edit gallery`,
      content: (
        <Form
          layout="vertical"
          initialValues={{ ...selectedGal }}
          form={galleryEditForm}
          onFinish={(data: any) => {
            ancanaApi.patch(`${basePath}/${selectedGal.id}/`, data).then(res => {
              fetchGalleries();
              editModal.destroy();
            });
          }}
        >
          <Form.Item label="Name" name="name" rules={[{ required: true }]} initialValue={selectedGal.name}>
            <Input />
          </Form.Item>
          <Form.Item label="Description en" name="description__en" rules={[{ required: false }]} initialValue={localizedTexts.find((locText: any) => locText.locale === 'en')?.text}>
            <Input.TextArea />
          </Form.Item>
          <Form.Item label="Description es" name="description__es" rules={[{ required: false }]}  initialValue={localizedTexts.find((locText: any) => locText.locale === 'es')?.text}>
            <Input.TextArea />
          </Form.Item>
        </Form>
      ),
      okText: "Save",
      icon: null,
      onOk: (close: any) => galleryEditForm.submit()
    });
  }

  useEffect(() => {
    fetchGalleries();
  }, [basePath, baseImgPath]);

  useEffect(() => {
    if (activeKey) {
      const galImages = allImages.filter(img => img.gallery == activeKey);
      setCurrentFiles(galImages);
    }
  }, [activeKey, allImages]);

  const handlePreview = async (file: any) => {
    const res = await ancanaApi.get(`localized-texts/?content_type=${galleryImgContentType?.id}&object_id=${file.uid}&page_size=50`) as any;
    const localizedTexts = res.data.results;
    setPreviewImage(file.hd_url);
    setPreviewImageFile(file);
    setPreviewOpen(true);
    imageEditForm.resetFields();
    imageEditForm.setFieldsValue({
      description__en: localizedTexts.find((lt: any) => lt.field_name === 'description' && lt.locale === 'en')?.text || '',
      description__es: localizedTexts.find((lt: any) => lt.field_name === 'description' && lt.locale === 'es')?.text || ''
    });
  };

  return (
    <Container>
      <DraggableTabs
        onEdit={handleTabEdit}
        // onTabClick={handleTabClick}
        onChange={(key: string) => setActiveKey(key)}
      >
        {galleries.map((gal: any) => (
          <Tabs.TabPane
            tab={gal.name}
            style={{ height: 420 }}
            key={gal.id}
          >
            <div
              style={{
                background: 'rgba(152,152,152,0.1)',
                padding: '6px',
                borderRadius: '2px',  // Ant Design v4 typically uses 2px radius
                marginBottom: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignContent: 'center',
              }}
            >
              {/* Left side - view controls */}
              <Space>
                <Radio.Group
                  optionType="button"
                  size="small"
                  options={[
                    { label: <AppstoreOutlined />, value: 'grid' },
                    { label: <UnorderedListOutlined />, value: 'list', disabled: true }
                  ]}
                />
              </Space>

              {/* Right side - action buttons */}
              <Space>
                <Button
                  size="small"
                  icon={<EditOutlined />}
                  onClick={() => handleTabClick(gal.id)}
                >
                  Edit Gallery
                </Button>
                <Button size="small" disabled icon={<InfoCircleOutlined />}></Button>
              </Space>
            </div>

            {(loadingGalleries || loadingImages) ? (
              <div className="disp-flex full-width pd-lg center">
                <Spin /> Loading images...
              </div>
            ) : (
              <DraggableUpload
                multiple
                action={`${process.env.REACT_APP_API_URL}/v1/${baseImgPath}/`}
                headers={ancanaApiHeaders}
                data={(file) => ({ image: file, gallery: gal.id })}
                listType="picture-card"
                fileList={currentFiles.sort(compareNumbers).map((galImg: any) => ({
                  galImg,
                  uid: galImg.id,
                  name: galImg.name,
                  status: !!galImg.status ? galImg.status : 'done',
                  percent: 99.9,
                  url: galImg.thumbnail,
                  hd_url: galImg.hd,
                  description: galImg.description
                }))}
                progress={{ status: 'active', showInfo: false, strokeWidth: 5 }}
                onSort={({ fileList }: any) => {
                  setCurrentFiles(fileList.map((f: any, idx: number) => {
                    return { ...f.galImg, sort_index: idx };
                  }));
                  let patchReqs: any = [];
                  fileList.forEach((f: any, idx: number) => {
                    patchReqs.push(ancanaApi.patch(`${baseImgPath}/${f.uid}/`, { sort_index: idx }));
                  });
                  axios.all(patchReqs).then(() => {
                    ancanaApi.get(baseImgPath).then(res => {
                      setAllImages(res.data);
                    });
                  });
                }}
                onChange={({ file }) => {
                  if (file.status !== 'removed') {
                    setAllImages(allImages => ([
                      ...allImages.filter((img: any) => img.id !== file.uid),
                      {
                        id: file.response ? (file.response as any).id : file.uid,
                        name: file.name,
                        status: file.response ? 'done' : 'uploading',
                        file: file.response ? (file.response as any).file : `none/${file.name}?`,
                        thumbnail: file.response ? (file.response as any).thumbnail : null,
                        hd: file.response ? (file.response as any).hd : null,
                        gallery: gal.id
                      },
                    ]));
                  }
                }}
                onPreview={handlePreview}
                onRemove={file => {
                  ancanaApi.delete(`${baseImgPath}/${file.uid}/`).then(res => {
                    if (res.status === 200) {
                      // setCurrentFiles(currentFiles.filter((cf: any) => cf.id !== file.uid));
                      setAllImages(allImages.filter(img => img.id !== file.uid));
                    }
                  });
                }}
              >
                <div>
                  <PlusOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              </DraggableUpload>
            )}
          </Tabs.TabPane>
        ))}
      </DraggableTabs>

      {!!previewImage &&
        <Modal
          width={900}
          visible={previewOpen}
          title="Image"
          footer={null}
          onCancel={() => {
            setPreviewOpen(false);
            setTimeout(() => setPreviewImage(undefined), 250);
          }}
        >
          <img alt="example" style={{ width: '100%', marginBottom: 16 }} src={previewImage} />

          <Form
            layout="vertical"
            form={imageEditForm}
            onFinish={(data) => {
              console.log(previewImageFile);
              ancanaApi.patch(`${baseImgPath}/${previewImageFile?.uid}/`, data).then(res => {
                ancanaApi.get(`localized-texts/?content_type=${galleryImgContentType?.id}&object_id=${previewImageFile.uid}&page_size=50`).then((res: any) => {
                  const localizedTexts = res.data.results;
                  imageEditForm.resetFields();
                  imageEditForm.setFieldsValue({
                    description__en: localizedTexts.find((lt: any) => lt.field_name === 'description' && lt.locale === 'en')?.text || '',
                    description__es: localizedTexts.find((lt: any) => lt.field_name === 'description' && lt.locale === 'es')?.text || ''
                  });
                  notification.success({ message: 'Image data saved', placement: 'bottomRight' });
                });

              });
            }}
          >
            <Form.Item
              name="description__en"
              label="Description english"
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item
              name="description__es"
              label="Description spanish"
            >
              <Input.TextArea />
            </Form.Item>
            <Form.Item>
            <Button type="primary" style={{ width: '33%', marginLeft: '33%' }} htmlType="submit">
                Save
              </Button>
            </Form.Item>
          </Form>
        </Modal>
      }
    </Container>
  )
}

export default RecordGalleries;
